import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Line, Bar, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import './App.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

const App = () => {
  const [question, setQuestion] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isFullScreenChat, setIsFullScreenChat] = useState(false);
  const chatEndRef = useRef(null);
  const textareaRef = useRef(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  // Generate a stable UUID session ID once per app session
  const sessionIdRef = useRef(crypto.randomUUID());

  // Scroll to bottom when chat history updates
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatHistory]);

  // Auto-resize textarea to fit content without a scrollbar
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      // Reset to auto so scrollHeight reflects true content height
      textarea.style.height = 'auto';
      const newHeight = Math.min(Math.max(textarea.scrollHeight, 60), 120);
      textarea.style.height = `${newHeight}px`;
    }
  }, [question]);

  // Focus on textarea when switching to full screen chat
  useEffect(() => {
    if (isFullScreenChat && textareaRef.current) {
      setTimeout(() => {
        textareaRef.current.focus();
      }, 300);
    }
  }, [isFullScreenChat]);


  const handleAskQuestion = async (q) => {
    const chat = q || question;
    if (!chat) {
      setError('Please enter a question');
      return;
    }
    setLoading(true);
    setError(null);

    const newChat = { question: chat, response: null };
    setChatHistory([...chatHistory, newChat]);

    try {
      const payload = {
        session_id: sessionIdRef.current,
        query: chat,
      };

      // Log equivalent curl command for debugging
      // console.log(
      //   `curl --location '${backendUrl}query' \\\n` +
      //   `--header 'Content-Type: application/json' \\\n` +
      //   `--data '${JSON.stringify(payload, null, 2)}'`
      // );

      const res = await axios.post(`${backendUrl}chat`, payload);
      const updatedChat = { question: chat, response: res.data };
      setChatHistory((prev) => [...prev.slice(0, -1), updatedChat]);
      setQuestion('');
      setIsFullScreenChat(true);
      
      // Additional scroll to bottom to ensure new message is visible
      setTimeout(() => {
        if (chatEndRef.current) {
          chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 200);
    } catch (err) {
      console.error('Error:', err);
      setError(
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        'Failed to get response'
      );
    } finally {
      setLoading(false);
    }
  };

  // Function removed since it's no longer used with header replacement

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAskQuestion();
    }
  };

  const handleTextareaChange = (e) => {
    setQuestion(e.target.value);
  };

  const renderChart = (response) => {
    if (!response || response.graph_needed !== 'yes') return null;
  
    const { graph_type, data } = response;
    
    if (!data || !data.labels || !data.datasets) {
      return <p className="no-data">No valid data available for chart</p>;
    }
  
    const chartData = {
      labels: data.labels || [],
      datasets: [],
    };
  
    const colors = [
      '#4b68a9','#ed5a3f', '#adb981', '#f8b797', '#FFEEAD',
      '#D4A5A5', '#9B59B6', '#3498DB', '#E74C3C', '#2ECC71',
    ];
    const BarChart_colors=[

      '#4b68a9'
    ]
  
    const options = {
      responsive: true,
      maintainAspectRatio: false,
      animation: {
        duration: 800,
        easing: 'easeOutQuart',
      },
      plugins: {
        legend: {
          display: true,
          position: 'bottom',
          align: 'center',
          labels: {
            color: '#000',
            font: { size: 12, weight: 'normal' },
            padding: 10,
            boxWidth: 20,
            boxHeight: 10,
            usePointStyle: false,
          },
        },
        title: {
          display: false,
          text: `${graph_type.replace('_', ' ').toUpperCase()} - Analysis`,
          color: '#000',
          font: { size: 14 },
        },
        tooltip: { 
          enabled: true,
          callbacks: {
            label: function(context) {
              let label = context.dataset.label || '';
              if (label) {
                label += ': ';
              }
              if (context.parsed.y !== null) {
                label += context.parsed.y.toLocaleString();
              }
              return label;
            }
          }
        },
        datalabels: {
          display: (context) => context.chart.config.type === 'bar',
          anchor: 'end',
          align: 'top',
          formatter: (value) => value.toLocaleString(),
          color: '#000',
          font: { weight: 'bold', size: 12 },
          offset: 5,
        },
      },
      scales: {},
    };
  
    switch (graph_type) {
      case 'line_chart':
        chartData.datasets = [{
          label: data.legend ? data.legend[0] : 'Value Over Time',
          data: data.datasets.map(item => item[0]),
          borderColor: colors[0],
          backgroundColor: `${colors[0]}33`,
          fill: true,
          tension: 0.4,
          pointStyle: 'circle',
        }];
        options.plugins.legend.display = false;
        options.scales = {
          y: {
            beginAtZero: true,
            title: { display: true, text: '', color: '#000', font: { size: 12 } },
            ticks: { 
              color: '#000', 
              font: { size: 10 },
              callback: function(value) {
                return value.toLocaleString();
              }
            },
            grid: { display: false },
            afterDataLimits: (scale) => {
              scale.max = scale.max * 1.1;
            }
          },
          x: {
            title: { display: true, text: '', color: '#000', font: { size: 12 } },
            ticks: { color: '#000', font: { size: 10 } },
            grid: { display: false },
          },
        };
        return (
          <div className="line-chart-wrapper">
            <Line data={chartData} options={options} />
          </div>
        );
  
      case 'bar_chart':
        chartData.datasets = [{
          label: data.legend ? data.legend[0] : 'Value',
          data: data.datasets.map(item => item[0]),
          backgroundColor: BarChart_colors.slice(0, data.labels.length),
          borderColor: BarChart_colors.slice(0, data.labels.length),
          borderWidth: 1,
          borderRadius: 4,
        }];
        options.plugins.legend.display = false;
        options.scales = {
          y: {
            beginAtZero: true,
            title: { display: true, text: '', color: '#000', font: { size: 12 } },
            ticks: { 
              color: '#000', 
              font: { size: 10 },
              callback: function(value) {
                return value.toLocaleString();
              }
            },
            grid: { display: false },
            afterDataLimits: (scale) => {
              scale.max = scale.max * 1.2;
            }
          },
          x: {
            title: { display: true, text: '', color: '#000', font: { size: 12 } },
            ticks: { color: '#000', font: { size: 10 } },
            grid: { display: false },
          },
        };
        return (
          <div className="bar-chart-wrapper">
            <Bar data={chartData} options={options} />
          </div>
        );
  
      case 'pie_chart':
        chartData.datasets = [{
          label: data.legend ? data.legend[0] : 'Value',
          data: data.datasets.map(item => item[0]),
          backgroundColor: chartData.labels.length <= colors.length
            ? colors.slice(0, chartData.labels.length)
            : [...colors, ...colors.slice(0, chartData.labels.length - colors.length)],
          borderColor: '#fff',
          borderWidth: 1,
        }];
        delete options.scales;
        options.plugins.datalabels = {
          display: true,
          formatter: (value, context) => {
            const total = context.dataset.data.reduce((sum, val) => sum + val, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${percentage}%`;
          },
          color: '#fff',
          font: { weight: 'bold', size: 12 },
          anchor: 'center',
          align: 'center',
        };
        
        options.plugins.tooltip = {
          callbacks: {
            label: function(context) {
              const total = context.dataset.data.reduce((sum, val) => sum + val, 0);
              const percentage = ((context.raw / total) * 100).toFixed(1);
              return `${context.label}: ${context.raw.toLocaleString()} (${percentage}%)`;
            }
          }
        };
        
        return (
          <div className="pie-chart-wrapper">
            <Pie data={chartData} options={options} />
          </div>
        );
  
      default:
        return <p className="no-data">Unsupported graph type: {graph_type}</p>;
    }
  };
  
  // Header component that will be used in both home and chat screens
  const Header = () => (
    <header className="app-header">
      <div className="logo">
        <img src="/Assets/logo.png" alt="People's Leasing Logo" />
      </div>
      <div className="header-icons">
        <div className="chatbot">
          <img src="/Assets/AI_ChatBot.png" alt="AI Chatbot" />
        </div>
      </div>
    </header>
  );

  return (
    <div className="app">
      {!isFullScreenChat && <Header />}

      <main className="app-main">
        {isFullScreenChat ? (
          <div className="full-screen-chat">
            
            {/* Replaced back button with the header */}
            <Header />
            <div className="chat-container">
              <div className="chat-messages full-screen-messages">
                {chatHistory.map((chat, idx) => (
                  <div key={idx} className="message-group">
                    <div className="message user-message">
                      <span className="message-content">{chat.question}</span>
                    </div>
                    <div className="message ai-message">
                      <div className="message-content-wrapper">
                        {chat.response ? (
                          <>
                            <span className="message-content">{chat.response.content || chat.response.text_answer}</span>
                            {chat.response.table_data && (
                              <div className="table-container">
                                <pre className="table-data">
                                  {JSON.stringify(chat.response.table_data, null, 2)}
                                </pre>
                              </div>
                            )}
                            {chat.response.graph_needed === 'yes' && (
                              <div className="chart-wrapper">
                                {renderChart(chat.response)}
                              </div>
                            )}
                          </>
                        ) : (
                          <span className="message-content thinking">
                            <span className="dot-typing"></span> Thinking...
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={chatEndRef} />
              </div>
            </div>
            <div className="chat-input-container">
              <div className="chat-input">
                <textarea
                  ref={textareaRef}
                  value={question}
                  onChange={handleTextareaChange}
                  placeholder="Ask another question..."
                  disabled={loading}
                  onKeyPress={handleKeyPress}
                />
                <button
                  onClick={() => handleAskQuestion()}
                  disabled={loading}
                  className="action-btn send-btn"
                  aria-label="Send message"
                >
                  {loading ? <span className="spinner"></span> : <img src="/Assets/send.png" alt="Send" className="send-icon" />}
                </button>
              </div>
              {error && <div className="error-message">{error}</div>}
            </div>
          </div>
        ) : (
          <>
            <h1>Welcome to People's Leasing AI Investor Assistant</h1>
            <h3>I'm here to help you with information about our financial and business performance</h3>

            <div className="home-card">
              {/* ── Input row at the top of the card ── */}
              <div className="home-input-row">
                <textarea
                  ref={textareaRef}
                  value={question}
                  onChange={handleTextareaChange}
                  placeholder="Ask a question about our financial performance..."
                  disabled={loading}
                  onKeyPress={handleKeyPress}
                />
                <button
                  onClick={() => handleAskQuestion()}
                  disabled={loading}
                  className="action-btn send-btn"
                  aria-label="Send message"
                >
                  {loading ? <span className="spinner"></span> : <img src="/Assets/send.png" alt="Send" className="send-icon" />}
                </button>
              </div>

              {error && <div className="error-message">{error}</div>}

              {/* ── Categories below the input ── */}
              <div className="categories">
                <div className="category">
                  <div className="category-header">
                    <div className="icon-wrapper">
                      <img src="/Assets/Growth.png" alt="Growth Icon" className="icon" />
                    </div>
                    <h2>Growth</h2>
                  </div>
                  <div className="question-box" onClick={() => handleAskQuestion("How did the overall business performance of the company grow during 2025/26?")}>How did the overall business performance of the company grow during 2025/26?</div>
                  <div className="question-box" onClick={() => handleAskQuestion("What were the key drivers of the company's growth in FY2025/26?")}>What were the key drivers of the company's growth in FY2025/26?</div>
                  <div className="question-box" onClick={() => handleAskQuestion("What is the growth outlook for the next year?")}>What is the growth outlook for the next year?</div>
                </div>

                <div className="category">
                  <div className="category-header">
                    <div className="icon-wrapper">
                      <img src="/Assets/Profitability.png" alt="Profitability Icon" className="icon" />
                    </div>
                    <h2>Profitability</h2>
                  </div>
                  <div className="question-box" onClick={() => handleAskQuestion("What was the company's profitability in 2025/26 compared to the previous year?")}>What was the company's profitability in 2025/26 compared to the previous year?</div>
                  <div className="question-box" onClick={() => handleAskQuestion("What is the ROE in 2025/26 and how does it compare to the previous year?")}>What is the ROE in 2025/26 and how does it compare to the previous year?</div>
                  <div className="question-box" onClick={() => handleAskQuestion("Replace this what is the total impairment charge for 2025/26 and its main drivers??")}>Replace this what is the total impairment charge for 2025/26 and its main drivers??</div>
                </div>

                <div className="category">
                  <div className="category-header">
                    <div className="icon-wrapper">
                      <img src="/Assets/portfolio.png" alt="Asset Quality Icon" className="icon" />
                    </div>
                    <h2>TOTAL Portfolio</h2>
                  </div>
                  <div className="question-box" onClick={() => handleAskQuestion("What Are the Key Drivers of the Lending Portfolio?")}>What Are the Key Drivers of the Lending Portfolio?</div>
                  <div className="question-box" onClick={() => handleAskQuestion("How Did the Lending Portfolio Perform in FY 2025/26 Compared to the Previous Year?")}>How Did the Lending Portfolio Perform in FY 2025/26 Compared to the Previous Year?</div>
                  <div className="question-box" onClick={() => handleAskQuestion("What is the non performing loan ratio for 2025/26 ans comparison to prior year?")}>What is the non performing loan ratio for 2025/26 ans comparison to prior year?</div>
                </div>
              </div>
            </div>

            {/* ── Annual Report Quantitative Data Hub Button ── */}
            <button
              className="student-api-guide-btn"
              id="student-api-guide-btn"
              onClick={() => window.open('/PLC_Dashboard/PLC_Dashboard.html', '_blank', 'noopener,noreferrer')}
            >
              <img
                src="/Assets/file.png"
                alt="Document"
                className="student-api-guide-icon"
              />
              Annual Report Quantitative Data Hub
            </button>
          </>
        )}
      </main>
    </div>
  );
};

export default App;